﻿import { ArrowLeft } from "lucide-react";
import { Link , useNavigate } from "react-router-dom";
import { toast } from "sonner";

import { DashboardShell } from "@/components/dashboard-shell";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { useAuthenticatedMe } from "@/features/organizers/api";
import { useCreateShopMutation } from "@/features/shops/api";
import { ShopForm, type CreateShopFormValues } from "@/features/shops/shop-form";
import { ApiError } from "@/lib/api-client";

export function NewShopPage() {
  const navigate = useNavigate();
  const me = useAuthenticatedMe();
  const create = useCreateShopMutation();

  async function handleSubmit(values: CreateShopFormValues) {
    try {
      const shop = await create.mutateAsync({
        name: values.name,
        description: emptyToNull(values.description),
        slug: emptyToNull(values.slug),
        defaultLocale: values.defaultLocale,
        // themeColor UI'dan kaldırıldı (v2 tema ayarları bölümünde dönecek).
        themeColor: null,
        contactEmail: emptyToNull(values.contactEmail),
        contactPhone: emptyToNull(values.contactPhone),
        // Yeni mağazalar varsayılan olarak Profil hero'su kullanır;
        // organizer sonradan mağaza ayarları → Tema Ayarları'ndan değiştirebilir.
        heroStyle: "Profile",
      });
      toast.success(`${shop.name} oluşturuldu.`);
      navigate(`/shops/${shop.slug}`);
    } catch (err) {
      if (err instanceof ApiError) {
        toast.error(err.detail);
      } else {
        toast.error("Mağaza oluşturulurken hata oluştu.");
      }
    }
  }

  if (!me.data) return null;

  return (
    <DashboardShell companyName={me.data.companyName}>
      <div className="mb-6">
        <Button variant="ghost" size="sm" asChild className="mb-3 -ml-2">
          <Link to="/">
            <ArrowLeft className="mr-1 h-4 w-4" />
            Dashboard
          </Link>
        </Button>
        <h1 className="text-2xl font-semibold">Yeni Mağaza</h1>
        <p className="text-sm text-muted-foreground">
          Farklı markalar veya lokasyonlar için birden fazla mağaza açabilirsin.
        </p>
      </div>

      <Card className="max-w-2xl">
        <CardHeader>
          <CardTitle className="text-lg">Mağaza Detayları</CardTitle>
          <CardDescription>
            Ad ve slug yeterli — diğer bilgileri sonra ekleyebilirsin.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <ShopForm
            mode="create"
            defaultValues={{ defaultLocale: me.data.preferredLocale ?? "tr" }}
            onSubmit={handleSubmit}
            isSubmitting={create.isPending}
          />
        </CardContent>
      </Card>
    </DashboardShell>
  );
}

function emptyToNull(value: string | null | undefined): string | null {
  if (value === null || value === undefined) return null;
  const trimmed = value.trim();
  return trimmed.length === 0 ? null : trimmed;
}
